<template>
  <div class="main-layout">
    <!-- 顶部导航栏 -->
    <nav class="navbar">
      <div class="nav-brand">
        <img :src="logoUrl" alt="全域AI" class="logo" />
      </div>

      <div class="nav-menu">
        <router-link v-for="item in navItems" :key="item.path" :to="item.path" class="nav-item"
          :class="{ active: $route.path === item.path }">
          {{ item.name }}
        </router-link>
      </div>

      <div class="nav-actions">
        <div class="user-info-dropdown">
          <el-dropdown @command="handleUserCommand">
            <div class="user-info">
              <el-avatar :size="32" :src="userAvatar" />
              <span class="username">{{ username }}</span>
              <el-icon class="arrow">
                <ArrowDown />
              </el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="modifyPassword">
                  <el-icon>
                    <Edit />
                  </el-icon>
                  <span>修改密码</span>
                </el-dropdown-item>
                <el-dropdown-item divided command="logout">
                  <el-icon>
                    <SwitchButton />
                  </el-icon>
                  <span>退出登录</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>

      <!-- 移动端菜单按钮 -->
      <div class="mobile-menu-btn" @click="toggleMobileMenu">
        <el-icon size="24">
          <Menu />
        </el-icon>
      </div>
    </nav>

    <!-- 移动端菜单 -->
    <div class="mobile-menu" :class="{ active: mobileMenuActive }">
      <div class="mobile-menu-content">
        <div class="mobile-menu-header">
          <span class="mobile-menu-title">菜单</span>
          <el-icon class="mobile-close-btn" @click="closeMobileMenu">
            <Close />
          </el-icon>
        </div>

        <div class="mobile-nav-items">
          <router-link v-for="item in navItems" :key="item.path" :to="item.path" class="mobile-nav-item"
            @click="closeMobileMenu" :class="{ active: $route.path === item.path }">
            <span class="mobile-nav-text">{{ item.name }}</span>
          </router-link>
        </div>

        <div class="mobile-user-section">
          <div class="mobile-user-info">
            <el-avatar :size="48" :src="userAvatar" class="mobile-user-avatar" />
            <div class="mobile-user-details">
              <span class="mobile-username">{{ username }}</span>
              <span class="mobile-user-role">用户</span>
            </div>
          </div>
          <div class="mobile-user-actions">
            <el-button text @click="handleMobileModifyPassword" class="mobile-action-btn">
              <el-icon>
                <Edit />
              </el-icon>
              <span>修改密码</span>
            </el-button>
            <el-button text @click="handleLogout" class="mobile-action-btn">
              <el-icon>
                <SwitchButton />
              </el-icon>
              <span>退出登录</span>
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 主内容区域 - 关键修改：使用 slot -->
    <main class="main-content-wrapper">
      <div class="main-content">
        <slot></slot>
      </div>
    </main>

    <!-- 底部信息修改 -->
    <footer class="footer">
      <div class="footer-divider"></div>
      <p class="copyright" v-html="copyrightText"></p>
    </footer>

    <!-- 修改密码对话框 -->
    <el-dialog v-model="showPasswordDialog" title="修改密码" width="400px" :close-on-click-modal="false"
      class="password-dialog">
      <el-form :model="passwordForm" :rules="passwordRules" ref="passwordFormRef" label-width="100px">
        <el-form-item label="当前密码" prop="oldPassword">
          <el-input v-model="passwordForm.oldPassword" type="password" placeholder="请输入当前密码" show-password />
        </el-form-item>
        <el-form-item label="新的密码" prop="newPassword">
          <el-input v-model="passwordForm.newPassword" type="password" placeholder="请输入新的密码" show-password />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="passwordForm.confirmPassword" type="password" placeholder="请再次输入新密码" show-password />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showPasswordDialog = false">取消</el-button>
        <el-button type="primary" @click="handleModifyPassword" class="confirm-btn">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, inject } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowDown, Edit, SwitchButton, Menu, Close } from '@element-plus/icons-vue'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import defaultLogo from '@/assets/logo.png'

export default {
  name: 'MainLayout',
  components: {
    ArrowDown,
    Edit,
    SwitchButton,
    Menu,
    Close
  },
  setup() {
    const router = useRouter()

    const webConfig = inject('webConfig', ref({
      pc_title: '全域AI',
      pc_logo: '',
      pc_ico: '',
      copyright_config: []
    }))

    // 计算属性
    const logoUrl = computed(() => webConfig.value.pc_logo || defaultLogo)
    const copyrightText = computed(() => {
      if (webConfig.value.copyright_config && webConfig.value.copyright_config.length > 0) {
        return webConfig.value.copyright_config.map(item => {
          if (item.value) {
            return `<a href="${item.value}" target="_blank" style="color: #ffffff; text-decoration: none;">${item.key}</a>`
          }
          return item.key
        }).join(' | ')
      }
      return 'Copyright 全域GEO推广 - Agent 2025 创客号：BICPB123123123号'
    })
    // 响应式状态
    const mobileMenuActive = ref(false)
    const showPasswordDialog = ref(false)

    // 导航项
    const navItems = [
      { path: '/', name: '首页' },
      { path: '/ai-portrait', name: 'AI画像' },
      // { path: '/ai-preparation', name: 'AI备课' },
      // { path: '/ai-teaching', name: 'AI授课' },
      { path: '/ai-agent', name: 'AI智能体' },
      { path: '/ai-distillation', name: 'AI蒸馏' },
      // { path: '/ai-report', name: 'AI成绩单' }
    ]

    // 用户信息
    const userInfo = computed(() => {
      try {
        return JSON.parse(Cookies.get('store_user_info') || '{}')
      } catch {
        return {}
      }
    })
    const username = computed(() => userInfo.value.phone || '用户')
    const userAvatar = ref('https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png')

    // 修改密码相关
    const passwordForm = ref({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    const passwordFormRef = ref()
    const passwordRules = {
      oldPassword: [{ required: true, message: '请输入当前密码', trigger: 'blur' }],
      newPassword: [
        { required: true, message: '请输入新的密码', trigger: 'blur' },
        { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
      ],
      confirmPassword: [
        { required: true, message: '请确认新密码', trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            if (value !== passwordForm.value.newPassword) {
              callback(new Error('两次输入的密码不一致'))
            } else {
              callback()
            }
          },
          trigger: 'blur'
        }
      ]
    }

    // 移动端菜单控制
    const toggleMobileMenu = () => {
      mobileMenuActive.value = !mobileMenuActive.value
    }

    const closeMobileMenu = () => {
      mobileMenuActive.value = false
    }

    // 用户命令处理
    const handleUserCommand = (command) => {
      switch (command) {
        case 'modifyPassword':
          showPasswordDialog.value = true
          break
        case 'logout':
          handleLogout()
          break
      }
    }

    // 移动端修改密码
    const handleMobileModifyPassword = () => {
      closeMobileMenu()
      showPasswordDialog.value = true
    }

    // 修改密码
    const handleModifyPassword = async () => {
      if (!passwordFormRef.value) return

      try {
        const valid = await passwordFormRef.value.validate()
        if (!valid) return

        const response = await axios.post(`${config.BASE_URL}/store/api/shop_api.php`, {
          action: 'update_password',
          store_id: userInfo.value.id, // 添加 store_id
          user_id: userInfo.value.id,  // 添加 user_id
          account: username.value,
          old_password: passwordForm.value.oldPassword,
          new_password: passwordForm.value.newPassword
        })

        if (response.data.success) {
          ElMessage.success('密码修改成功')
          showPasswordDialog.value = false
          passwordForm.value = {
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
          }
        } else {
          ElMessage.error(response.data.message || '密码修改失败')
        }
      } catch (error) {
        console.error('修改密码失败:', error)
        ElMessage.error('修改密码失败')
      }
    }

    // 退出登录
    const handleLogout = async () => {
      try {
        await ElMessageBox.confirm('确定要退出登录吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        Cookies.remove('store_user_token')
        Cookies.remove('store_user_info')
        ElMessage.success('已退出登录')
        closeMobileMenu()
        router.push('/')
      } catch {
        // 用户取消
      }
    }

    onMounted(() => {
      // 可以在这里加载用户额外信息
    })

    return {
      logoUrl, // 替换原来的 logo
      copyrightText, // 新增
      navItems,
      username,
      userAvatar,
      mobileMenuActive,
      showPasswordDialog,
      passwordForm,
      passwordFormRef,
      passwordRules,
      handleUserCommand,
      handleMobileModifyPassword,
      handleModifyPassword,
      handleLogout,
      toggleMobileMenu,
      closeMobileMenu
    }
  }
}
</script>

<style scoped lang="scss">
.main-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: hidden;
}

/* 底部链接样式 */
.copyright {
  a {
    color: #ffffff !important;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #6e21fe !important;
      text-decoration: underline;
    }
  }
}

// 背景效果
.main-layout::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 20% 80%, rgba(110, 33, 254, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(192, 132, 252, 0.05) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
}

.main-layout::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(rgba(110, 33, 254, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(110, 33, 254, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  pointer-events: none;
  z-index: 0;
}

// 导航栏
.navbar {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  height: 70px;
  background: rgba(13, 5, 26, 0.95);
  backdrop-filter: blur(15px);
  border-bottom: 1px solid rgba(110, 33, 254, 0.3);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px;
  z-index: 1000;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);

  .nav-brand {
    display: flex;
    align-items: center;
    gap: 12px;

    .logo {
      height: 45px;
      width: auto;
      border-radius: 8px;
    }

    .brand-name {
      font-size: 20px;
      font-weight: 700;
      background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      text-shadow: 0 2px 10px rgba(110, 33, 254, 0.3);
    }
  }

  .nav-menu {
    display: flex;
    align-items: center;
    gap: 8px;

    .nav-item {
      padding: 12px 20px;
      color: #a0a8b8;
      text-decoration: none;
      border-radius: 8px;
      transition: all 0.3s ease;
      font-weight: 500;
      position: relative;
      white-space: nowrap;

      &:hover {
        color: #6e21fe;
        background: rgba(110, 33, 254, 0.1);
      }

      &.active {
        color: #ffffff;
        background: #6e21fe !important;
        box-shadow: 0 4px 15px rgba(110, 33, 254, 0.4);

        &::after {
          content: '';
          position: absolute;
          bottom: -11px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid #6e21fe;
        }
      }
    }
  }

  .nav-actions {
    .user-info-dropdown {
      .user-info {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px 16px;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.3s ease;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(110, 33, 254, 0.2);

        &:hover {
          background: rgba(110, 33, 254, 0.1);
          border-color: rgba(110, 33, 254, 0.4);
        }

        .username {
          color: #e0e6ff;
          font-weight: 600;
          font-size: 14px;
        }

        .arrow {
          color: #a0a8b8;
          font-size: 12px;
        }
      }
    }
  }

  .mobile-menu-btn {
    display: none;
    cursor: pointer;
    color: #e0e6ff;
    padding: 8px;
    border-radius: 6px;
    transition: all 0.3s ease;
    z-index: 1002;

    &:hover {
      background: rgba(110, 33, 254, 0.1);
    }
  }
}

// 移动端菜单
.mobile-menu {
  position: fixed;
  top: 0;
  right: -100%;
  width: 85vw;
  max-width: 200px;
  height: 100vh;
  background: rgba(13, 5, 26, 0.98);
  backdrop-filter: blur(20px);
  border-left: 1px solid rgba(110, 33, 254, 0.3);
  z-index: 1001;
  transition: right 0.3s ease;
  display: flex;

  &.active {
    right: 0;
  }

  .mobile-menu-content {
    flex: 1;
    padding: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    width: 100%;

    .mobile-menu-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px;
      border-bottom: 1px solid rgba(110, 33, 254, 0.2);
      background: rgba(110, 33, 254, 0.1);

      .mobile-menu-title {
        color: #e0e6ff;
        font-size: 18px;
        font-weight: 600;
      }

      .mobile-close-btn {
        color: #a0a8b8;
        cursor: pointer;
        padding: 4px;
        width: 40px;
        height: 40px;
        border-radius: 4px;
        transition: all 0.3s ease;

        &:hover {
          color: #6e21fe;
          background: rgba(110, 33, 254, 0.1);
        }
      }
    }

    .mobile-nav-items {
      flex: 1;
      padding: 20px;
    }

    .mobile-nav-item {
      display: flex;
      align-items: center;
      padding: 15px 20px;
      color: #a0a8b8;
      text-decoration: none;
      border-radius: 8px;
      margin-bottom: 8px;
      transition: all 0.3s ease;
      font-weight: 500;
      text-align: left;
      width: 100%;

      &:hover,
      &.active {
        color: #ffffff;
        background: #6e21fe;
        box-shadow: 0 4px 15px rgba(110, 33, 254, 0.4);
      }

      .mobile-nav-text {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .mobile-user-section {
      margin-top: auto;
      padding: 20px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(255, 255, 255, 0.05);

      .mobile-user-info {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 20px;

        .mobile-user-avatar {
          flex-shrink: 0;
          border: 2px solid rgba(110, 33, 254, 0.3);
        }

        .mobile-user-details {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .mobile-username {
            color: #e0e6ff;
            font-weight: 600;
            font-size: 16px;
          }

          .mobile-user-role {
            color: #a0a8b8;
            font-size: 12px;
          }
        }
      }

      .mobile-user-actions {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .mobile-action-btn {
          justify-content: flex-start !important;
          padding: 12px 0 !important;
          width: 100%;
          margin-left: 0 !important;
          color: #a0a8b8;

          &:hover {
            color: #6e21fe;
            background: rgba(110, 33, 254, 0.1);
          }

          span {
            display: flex;
            align-items: center;
            gap: 8px;
            justify-content: flex-start;
            width: 100%;
          }
        }
      }
    }
  }
}

// 主内容区域包装器
.main-content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}

// 主内容区域
.main-content {
  flex: 1;
  padding: 30px;
  position: relative;
  z-index: 1;
}

// 底部信息
.footer {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 30px 20px;
  margin-top: auto;

  .footer-divider {
    border-top: 1px dashed #3a3a3e;
    margin: 0 0 20px 0;
  }

  .copyright {
    margin: 0;
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
  }
}

// 修改密码对话框样式
.password-dialog {
  :deep(.el-dialog) {
    background: rgba(13, 5, 26, 0.95) !important;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(110, 33, 254, 0.3);
    border-radius: 16px;
    box-shadow:
      0 20px 60px rgba(0, 0, 0, 0.5),
      0 0 0 1px rgba(110, 33, 254, 0.1);

    .el-dialog__header {
      background: rgba(110, 33, 254, 0.1);
      border-bottom: 1px solid rgba(110, 33, 254, 0.2);
      margin: 0;
      padding: 20px;

      .el-dialog__title {
        color: #e0e6ff !important;
        font-weight: 600;
        background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    }

    .el-dialog__body {
      padding: 30px;

      .el-form-item__label {
        color: #e0e6ff !important;
        font-weight: 600 !important;
        font-size: 14px !important;
        margin-bottom: 8px !important;
        display: block !important;
      }

      .el-input__wrapper {
        background: rgba(255, 255, 255, 0.08);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(110, 33, 254, 0.3);
        border-radius: 8px;
        transition: all 0.3s ease;

        &:hover {
          border-color: #6e21fe;
          box-shadow: 0 4px 15px rgba(110, 33, 254, 0.2);
        }

        &.is-focus {
          border-color: #6e21fe;
          box-shadow: 0 4px 20px rgba(110, 33, 254, 0.3);
        }

        .el-input__inner {
          color: #e0e6ff !important;
          background: transparent !important;

          &::placeholder {
            color: #64748b !important;
          }
        }
      }
    }

    .el-dialog__footer {
      padding: 20px 30px 30px;
      border-top: 1px solid rgba(110, 33, 254, 0.1);

      .el-button {
        border-radius: 8px;
        font-weight: 600;
        transition: all 0.3s ease;

        &.confirm-btn {
          background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
          border: none;
          box-shadow: 0 4px 15px rgba(110, 33, 254, 0.3);

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(110, 33, 254, 0.4);
          }
        }
      }
    }
  }
}

// 响应式设计
@media (max-width: 1024px) {
  .navbar {
    .nav-menu {
      gap: 4px;

      .nav-item {
        padding: 10px 16px;
        font-size: 14px;
      }
    }
  }
}

@media (max-width: 768px) {
  .navbar {
    padding: 0 20px;

    .nav-menu {
      display: none;
    }

    .nav-brand .brand-name {
      font-size: 18px;
    }

    .mobile-menu-btn {
      display: block;
    }
  }

  .main-content {
    padding: 20px;
  }

  .footer {
    padding: 25px 15px;

    .copyright {
      font-size: 14px;
    }
  }
}

@media (max-width: 480px) {
  .navbar {
    padding: 0 15px;

    .nav-brand {
      .brand-name {
        font-size: 16px;
      }

      .logo {
        height: 35px;
      }
    }
  }

  .main-content {
    padding: 15px;
  }

  .footer {
    padding: 20px 10px;

    .copyright {
      font-size: 13px;
    }
  }

  .mobile-menu {
    width: 85vw;

    .mobile-menu-content {
      .mobile-nav-item {
        padding: 12px 16px;

      }

      .mobile-user-section {
        padding: 15px;
      }
    }
  }
}
</style>